import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import LuvmeIcon from "@/components/LuvmeIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0D0D0D] px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <LuvmeIcon className="w-7 h-7" />
          <span className="text-2xl font-black font-heading">Luvme</span>
        </Link>

        {result ? (
          <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-7 text-center" data-testid="forgot-success">
            <MailCheck className="w-10 h-10 text-[#E91E8C] mx-auto mb-4" />
            <h1 className="text-xl font-black font-heading mb-2">Controlla la tua email</h1>
            <p className="text-white/60 text-sm">{result.message}</p>
            {result.demo_reset_link && (
              <div className="mt-5 text-left bg-amber-400/10 border border-amber-400/20 rounded-xl p-3" data-testid="forgot-demo-link">
                <p className="text-xs text-amber-300/90 mb-2">Modalità demo — apri il link per reimpostare la password:</p>
                <Link to={result.demo_reset_link.replace(/^https?:\/\/[^/]+/, "")} className="text-[#E91E8C] text-sm break-all hover:underline" data-testid="forgot-demo-reset-btn">
                  {result.demo_reset_link}
                </Link>
              </div>
            )}
            <Link to="/login" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mt-6" data-testid="back-to-login">
              <ArrowLeft className="w-4 h-4" /> Torna al login
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-black font-heading mb-2">Recupera la password</h1>
            <p className="text-white/60 mb-8">Inserisci la tua email: ti invieremo un link per reimpostarla.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <Input
                  id="email" type="email" required value={email} data-testid="forgot-email"
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 bg-[#1A1A1A] border-white/10 h-12 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                  placeholder="tu@esempio.com"
                />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="forgot-error">{error}</p>}
              <Button
                type="submit" disabled={loading} data-testid="forgot-submit"
                className="w-full h-12 rounded-xl bg-[#E91E8C] hover:bg-[#D8157B] text-white font-semibold hover:shadow-[0_0_18px_rgba(233,30,140,0.5)] transition-all disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia link di recupero"}
              </Button>
            </form>
            <p className="text-center mt-6">
              <Link to="/login" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2" data-testid="back-to-login">
                <ArrowLeft className="w-4 h-4" /> Torna al login
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
